// renderer/BlockRenderer.jsx
// CORE ENGINE — renders any block type from JSON data
// Add new block types here as you expand the platform

import { useState, useEffect } from "react";
import "../styles/blocks.css";

// ── Text ──────────────────────────────────────────────────────────────────────
const TextBlock = ({ data }) => (
  <div className="block-text">
    {(data.paragraphs || [data.content || data.html || ""].filter(Boolean)).map(
      (p, i) => (
        <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
      ),
    )}
  </div>
);

// ── Story ─────────────────────────────────────────────────────────────────────
const StoryBlock = ({ data }) => (
  <div className="block-story">
    {(data.paragraphs || []).map((p, i) => (
      <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
    ))}
  </div>
);

// ── Concept ───────────────────────────────────────────────────────────────────
const ConceptBlock = ({ data }) => (
  <div className="block-concept">
    {data.label && <div className="block-concept-label">{data.label}</div>}
    <div className="block-concept-title">{data.title}</div>
    <p style={{ whiteSpace: 'pre-wrap' }}>{data.body}</p>
    {data.analogy && (
      <div className="block-concept-analogy" dangerouslySetInnerHTML={{ __html: data.analogy }} />
    )}
  </div>
);

// ── Logic Flow ────────────────────────────────────────────────────────────────
const LogicBlock = ({ data }) => (
  <div className="block-logic">
    {data.label && <div className="block-logic-label">{data.label}</div>}
    <div className="logic-flow">
      <div className="lf-col">
        <div className="lf-box lf-input">{data.input}</div>
        <div className="lf-sublabel">Input</div>
      </div>
      <div className="lf-arrow">→</div>
      <div className="lf-col">
        <div className="lf-box lf-decision">{data.decision}</div>
        <div className="lf-sublabel">Decision</div>
      </div>
      <div className="lf-arrow">→</div>
      <div className="lf-col">
        <div className="lf-box lf-yes">{data.outputYes}</div>
        <div className="lf-box lf-no" style={{ marginTop: 4 }}>
          {data.outputNo}
        </div>
        <div className="lf-sublabel">Output</div>
      </div>
    </div>
  </div>
);

// ── Code ──────────────────────────────────────────────────────────────────────
const colorMap = {
  comment: "cl-comment",
  kw: "cl-kw",
  fn: "cl-fn",
  str: "cl-str",
  val: "cl-val",
  normal: "cl-normal",
  hl: "cl-normal",
};

const CodeBlock = ({ data }) => (
  <div className="block-code">
    {data.label && <div className="block-code-label">{data.label}</div>}
    <div className="code-window">
      <div className="code-window-bar">
        <div className="code-dot code-dot-r" />
        <div className="code-dot code-dot-y" />
        <div className="code-dot code-dot-g" />
        {data.lang && (
          <span className="code-window-lang">{data.lang}</span>
        )}
      </div>
      <div className="code-window-body">
        {(data.lines || []).map((line, i) => {
          if (!line || typeof line !== 'object') return null;
          return (
            <div
              key={i}
              className={`code-line ${line.t === "hl" ? "code-hl" : ""} ${colorMap[line.t] || "cl-normal"}`}
            >
              {line.v !== "" ? line.v : " "}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

// ── Quiz ──────────────────────────────────────────────────────────────────────
const QuizBlock = ({ data }) => {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  return (
    <div className="block-quiz">
      <div className="quiz-label">Quick check</div>
      <div className="quiz-question">{data.question}</div>
      <div className="quiz-options">
        {(data.options || []).map((opt, i) => {
          let cls = "quiz-opt";
          if (answered) {
            if (i === data.correct) cls += " correct";
            else if (i === selected) cls += " wrong";
          }
          return (
            <button
              key={i}
              className={cls}
              onClick={() => !answered && setSelected(i)}
              disabled={answered}
            >
              <span>{String.fromCharCode(97 + i)})</span> {opt}
            </button>
          );
        })}
      </div>
      {answered && (
        <div
          className={`quiz-feedback ${selected === data.correct ? "ok" : "no"}`}
        >
          {selected === data.correct ? "✓ " : "✗ "}
          {data.feedback}
        </div>
      )}
    </div>
  );
};

// ── Callout ───────────────────────────────────────────────────────────────────
const CALLOUT_ICONS = {
  blue: "💡",
  amber: "⚡",
  green: "✅",
  purple: "🔮",
};

const CalloutBlock = ({ data }) => {
  // Card-with-rows rendering: white card + icon/text/tag rows
  if (data.items && data.items.length > 0) {
    return (
      <div className="block-callout-card">
        {data.label && <div className="callout-card-title">{data.label}</div>}
        {data.items.map((item, i) => (
          <div key={i} className="thought-item">
            {item.icon && <span className="thought-icon">{item.icon}</span>}
            <span className="thought-text">{item.text}</span>
            {item.tag && (
              <span className={`thought-tag tag-${item.color || "blue"}`}>{item.tag}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Legacy border-style rendering
  const color = data.color;
  const variant = data.variant;
  const isNew = !!color;
  const cls = isNew
    ? `block-callout callout-${color}`
    : `block-callout callout-${variant || "insight"}`;

  const body = data.content ?? data.html ?? data.text ?? "";
  const hasHtml = typeof body === "string" && body.includes("<");

  return (
    <div className={cls}>
      {isNew ? (
        <>
          {(data.label || CALLOUT_ICONS[color]) && (
            <div className="callout-head">
              <span className="callout-icon">{CALLOUT_ICONS[color]}</span>
              {data.label && (
                <span className="callout-label">{data.label}</span>
              )}
            </div>
          )}
          <div className="callout-body">
            {hasHtml ? (
              <span dangerouslySetInnerHTML={{ __html: body }} />
            ) : (
              body
            )}
          </div>
        </>
      ) : (
        <>
          {data.label && (
            <div className="callout-legacy-label">{data.label}</div>
          )}
          {hasHtml ? (
            <div dangerouslySetInnerHTML={{ __html: body }} />
          ) : (
            <div>{body}</div>
          )}
        </>
      )}
    </div>
  );
};

// ── Image ─────────────────────────────────────────────────────────────────────
const ImageBlock = ({ data }) => {
  const url = data?.url ?? data?.src;
  const title = data?.title ?? "Reference Image";
  const caption = data?.caption;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  if (!url) return null;

  return (
    <div className="block-image">
      <div
        style={{ borderRadius: 10, overflow: "hidden", border: "1.5px solid #e8e2d6", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", cursor: "pointer" }}
        onClick={() => setLightboxOpen(true)}
      >
        <img src={url} alt={title} loading="lazy" style={{ width: "100%", display: "block" }} />
      </div>
      {caption && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, padding: "0 4px" }}>
          <span style={{ fontSize: 13, color: "#8a7f6e", fontStyle: "italic", lineHeight: 1.6 }}>{caption}</span>
        </div>
      )}

      {lightboxOpen && (
        <div
          onClick={() => setLightboxOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            style={{
              position: "absolute",
              top: 20,
              right: 24,
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 28,
              lineHeight: 1,
              padding: 4,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
          <img
            src={url}
            alt={title}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: 6,
              boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
            }}
          />
        </div>
      )}
    </div>
  );
};

// ── Exercise (singular) ───────────────────────────────────────────────────────
const ExerciseBlock = ({ data }) => {
  const [revealed, setRevealed] = useState(false);
  const prompt = data.prompt;
  const output = data.output;
  const code = data.code;
  const num = data.number;

  return (
    <div className="block-exercise">
      <div className="block-exercise-header">
        <span className="block-exercise-header-icon">✏️</span>
        <span className="block-exercise-header-label">Try it yourself</span>
        {num && <span className="block-exercise-num">#{num}</span>}
      </div>
      <div className="block-exercise-body">
        {prompt && <div className="block-exercise-prompt">{prompt}</div>}
        {output && (
          <div className="block-exercise-output">
            <div className="block-exercise-output-label">Expected output</div>
            <div className="block-exercise-output-text">{output}</div>
          </div>
        )}
      </div>
      {code && (
        <>
          <button
            className="block-exercise-reveal"
            onClick={() => setRevealed((r) => !r)}
          >
            <span>{revealed ? "🙈" : "👁"}</span>
            {revealed ? "Hide solution code" : "Show solution code"}
          </button>
          {revealed && (
            <div className="block-exercise-code">
              <div className="code-window" style={{ borderRadius: 0 }}>
                <div className="code-window-bar">
                  <div className="code-dot code-dot-r" />
                  <div className="code-dot code-dot-y" />
                  <div className="code-dot code-dot-g" />
                </div>
                <div
                  className="code-window-body"
                  dangerouslySetInnerHTML={{ __html: code }}
                  style={{ whiteSpace: "pre-wrap" }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── CTA ───────────────────────────────────────────────────────────────────────
const CtaBlock = ({ data }) => (
  <div className="block-cta">
    {data.badge && <div className="cta-badge">{data.badge}</div>}
    <h3>{data.title}</h3>
    <p>{data.body}</p>
    <a
      href={data.btnUrl}
      className="cta-btn"
      target="_blank"
      rel="noopener noreferrer"
    >
      {data.btnLabel} →
    </a>
  </div>
);

// ── Thoughts ──────────────────────────────────────────────────────────────────
const THOUGHT_ICONS = {
  check: "✓",
  cross: "✗",
  arrow: "→",
  star: "★",
};

const ThoughtsBlock = ({ data }) => (
  <div className="block-thoughts">
    {(data.items || []).map((item, i) => (
      <div key={i} className="thought-item">
        <span className="thought-icon">
          {THOUGHT_ICONS[item.icon] || item.icon || "✓"}
        </span>
        <span className="thought-text">{item.text}</span>
        <span className={`thought-tag tag-${item.color || "blue"}`}>
          {item.tag}
        </span>
      </div>
    ))}
  </div>
);

// ── Table ─────────────────────────────────────────────────────────────────────
const TableBlock = ({ data }) => (
  <div className="block-table">
    <table>
      <thead>
        <tr>
          {(data.headers || []).map((h, i) => (
            <th key={i}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(data.rows || []).map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── VarBoxes ──────────────────────────────────────────────────────────────────
const VarBoxesBlock = ({ data }) => (
  <div className="block-varboxes">
    {(data.items || []).map((item, i) => (
      <div key={i} className="var-box">
        <div className="var-box-type">{item.type}</div>
        <div className="var-box-name">{item.name}</div>
        <div className={`var-box-value${item.green ? " green" : ""}`}>
          {item.value}
        </div>
      </div>
    ))}
  </div>
);

// ── Before/After ──────────────────────────────────────────────────────────────
const BeforeAfterBlock = ({ data }) => {
  const renderCol = (col, side) => (
    <div className={`ba-col ${side}`}>
      <div className="ba-col-label">{col.label}</div>
      <ul>
        {(col.items || []).map((item, i) => {
          const isLast = i === col.items.length - 1;
          const cls =
            isLast && col.lastRed
              ? "last-red"
              : isLast && col.lastGreen
                ? "last-green"
                : "";
          return (
            <li key={i} className={cls}>
              {item}
            </li>
          );
        })}
      </ul>
    </div>
  );
  return (
    <div className="block-beforeafter">
      {renderCol(data.before, "before")}
      {renderCol(data.after, "after")}
    </div>
  );
};

// ── Timeline ──────────────────────────────────────────────────────────────────
const TimelineBlock = ({ data }) => (
  <div className="block-timeline">
    {(data.steps || []).map((step, i) => (
      <div key={i} className="tl-step">
        <div className={`tl-dot${step.done ? " done" : ""}`}>{step.number}</div>
        <div className="tl-content">
          <div className="tl-title">{step.title}</div>
          <div className="tl-desc">{step.desc}</div>
        </div>
      </div>
    ))}
  </div>
);

// ── Exercises (plural collection) ─────────────────────────────────────────────
const ExercisesBlock = ({ data }) => {
  const [open, setOpen] = useState({});
  return (
    <div className="block-exercises">
      {(data.items || []).map((ex, i) => (
        <div key={i} className="ex-card">
          <div className="ex-num">{ex.number}</div>
          <div className="ex-task">{ex.task}</div>
          <div className="ex-hint">{ex.hint}</div>
          <button
            className="ex-toggle"
            onClick={() => setOpen((s) => ({ ...s, [i]: !s[i] }))}
          >
            {open[i] ? "Hide" : "See the logic"}
          </button>
          {open[i] && (
            <div
              className="ex-answer"
              dangerouslySetInnerHTML={{ __html: ex.answer }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// ── Checklist ─────────────────────────────────────────────────────────────────
const ChecklistBlock = ({ data }) => (
  <div className="block-checklist">
    {(data.items || []).map((item, i) => (
      <div key={i} className="check-item">
        <div
          className={`check-icon icon-${item.yes === true ? "yes" : item.yes === "arrow" ? "arrow" : "no"}`}
        >
          {item.yes === true ? "✓" : item.yes === "arrow" ? "→" : "✕"}
        </div>
        <span dangerouslySetInnerHTML={{ __html: item.text }} />
      </div>
    ))}
  </div>
);

const HeadingBlock = ({ data }) => (
  <div className="block-heading">
    {data.eyebrow && (
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "#9a8f7e", marginBottom: 6 }}>
        {data.eyebrow}
      </div>
    )}
    <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: "1.15rem", fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px 0", lineHeight: 1.3 }}>
      {data.title}
    </h3>
    {data.divider && <div style={{ height: 1, background: "#e8e2d6", marginTop: 12 }} />}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MASTER RENDERER — switch on block.type
// ═══════════════════════════════════════════════════════════════════════════════
const BlockRenderer = ({ block }) => {
  if (!block) return null;
  const { type, data } = block;

  switch (type) {
    case "text":
      return <TextBlock data={data} />;
    case "story":
      return <StoryBlock data={data} />;
    case "concept":
      return <ConceptBlock data={data} />;
    case "logic":
      return <LogicBlock data={data} />;
    case "code":
      return <CodeBlock data={data} />;
    case "quiz":
      return <QuizBlock data={data} />;
    case "callout":
      return <CalloutBlock data={data} />;
    case "image":
      return <ImageBlock data={block} />;
    case "exercise":
      return <ExerciseBlock data={data} />;
    case "cta":
      return <CtaBlock data={data} />;
    case "thoughts":
      return <ThoughtsBlock data={data} />;
    case "table":
      return <TableBlock data={data} />;
    case "varboxes":
      return <VarBoxesBlock data={data} />;
    case "beforeafter":
      return <BeforeAfterBlock data={data} />;
    case "timeline":
      return <TimelineBlock data={data} />;
    case "exercises":
      return <ExercisesBlock data={data} />;
    case "checklist":
      return <ChecklistBlock data={data} />;
    case "heading":
      return <HeadingBlock data={data} />;
    default:
      return (
        <div
          style={{
            padding: "1rem",
            background: "#fef2f2",
            borderRadius: 8,
            fontSize: 13,
            color: "#991b1b",
          }}
        >
          Unknown block type: <code>{type}</code>
        </div>
      );
  }
};

export default BlockRenderer;
